import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { SendTestEmailComponent } from '@app/management/dialogs/send-test-email/send-test-email.component';
import { UnlayerEmailEditorComponent } from '@app/shared/components/unlayer-email-editor/unlayer-email-editor.component';
import { ClinicEmailTemplate, EmailTemplateType, getEmailTemplateType } from '@models/clinic-email-template';
import { CommunicationSettingType } from '@models/communications/communication-settings';
import { EmailCommunicationSetting } from '@models/communications/email-notifications-settings.model';
import { CommunicationsSettingBase } from '@models/communications/notifications-settings-base.model';
import { editorOptions } from '@models/email-editor-options';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ClinicEmailTemplateService } from '@services/clinic-email-template.service';
import { CommunicationsService } from '@services/communications.service';
import moment from 'moment';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-edit-email-reminder',
  templateUrl: './edit-email-reminder.component.html',
  styleUrls: ['./edit-email-reminder.component.less'],
})
export class EditEmailReminderComponent implements OnInit, OnDestroy {
  private unsub: Subject<void> = new Subject<void>();
  loading = false;
  templateLoading = false;
  showSuccessMessage = false;
  iPad =
    navigator.userAgent.match(/(iPad)/) /* iOS pre 13 */ ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) /* iPad OS 13 */;
  setting: EmailCommunicationSetting;
  minEndTime = moment().toDate();
  minimumDuration: number = 5;
  senderUnits: string[] = ['hours', 'days', 'weeks', 'months'];
  workerStartTime = moment().startOf('day').toDate();
  workerEndTime = moment().startOf('day').toDate();
  defaultStartTime = '7:00:00';
  defaultEndTime = '19:00:00';
  emailTemplates: ClinicEmailTemplate[] = [];
  @ViewChild(UnlayerEmailEditorComponent) emailEditor: UnlayerEmailEditorComponent;
  editorOptions = editorOptions;
  emailDesignDef: object;
  CommunicationSettingType = CommunicationSettingType;

  reminderSettingsForm = new FormGroup({
    emailSenderName: new FormControl('', Validators.required),
    emailSenderEmail: new FormControl('', [Validators.email, Validators.required]),
    emailSubjectLine: new FormControl('', Validators.required),
    firstNotificationPeriod: new FormControl('', Validators.required),
    firstNotificationUnit: new FormControl('', Validators.required),
    repeatNotificationPeriod: new FormControl('', Validators.required),
    repeatNotificationUnit: new FormControl('', Validators.required),
    enabledRepeatNotification: new FormControl(true),
    template: new FormControl(null),
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private communicationsService: CommunicationsService,
    private clinicEmailTemplateService: ClinicEmailTemplateService,
    private zone: NgZone,
    private dialog: MatDialog,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loading = true;

    this.reminderSettingsForm
      .get('template')
      .valueChanges.pipe(takeUntil(this.unsub))
      .subscribe((template: ClinicEmailTemplate) => {
        this.loadEmailDef(template.definition);
      });

    this.reminderSettingsForm.controls['enabledRepeatNotification'].valueChanges.subscribe((status: boolean) => {
      this.reminderSettingsForm.controls['repeatNotificationPeriod'].setValidators(
        status ? [Validators.required] : null
      );
      this.reminderSettingsForm.controls['repeatNotificationUnit'].setValidators(status ? [Validators.required] : null);
      this.reminderSettingsForm.controls['repeatNotificationPeriod'].updateValueAndValidity();
      this.reminderSettingsForm.controls['repeatNotificationUnit'].updateValueAndValidity();
    });

    this.route.params
      .pipe(
        takeUntil(this.unsub),
        switchMap((params) => {
          const settingId = params['id'];
          return this.communicationsService.getEmailSettingById(settingId);
        }),
        switchMap((setting) => {
          const emailTemplateType = getEmailTemplateType(setting.communicationSettingType);
          return this.clinicEmailTemplateService
            .getClinicEmailTemplatesByType(emailTemplateType)
            .map((templates) => [setting, templates]);
        })
      )
      .subscribe(
        ([setting, emailTemplates]: [EmailCommunicationSetting, ClinicEmailTemplate[]]) => {
          this.emailTemplates = emailTemplates;
          this.setting = setting;
          this.populateEmailSettingsValues(setting);
          this.populateCommonSettingsValues(setting);
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
  }

  populateEmailSettingsValues(settings: EmailCommunicationSetting) {
    this.loadEmailDef(settings.emailJson);
    const selectedTemplate = this.emailTemplates.find((_) => _.id === settings.createdFromEmailTemplateId) ?? null;
    this.reminderSettingsForm.controls['emailSenderName'].setValue(settings.senderName);
    this.reminderSettingsForm.controls['emailSenderEmail'].setValue(settings.senderEmail);
    this.reminderSettingsForm.controls['emailSubjectLine'].setValue(settings.emailSubjectLine);
    this.reminderSettingsForm.controls['template'].setValue(selectedTemplate, { emitEvent: false });
  }

  loadEmailDef(definition: string): void {
    if (!definition) return;
    this.emailDesignDef = JSON.parse(definition);
    this.emailEditor.editor.loadDesign(this.emailDesignDef);
  }

  populateCommonSettingsValues(settings: CommunicationsSettingBase) {
    if (settings.workerStartTime.hours() == 0) {
      settings.workerStartTime = moment.duration(this.defaultStartTime);
    }

    if (settings.workerEndTime.hours() == 0) {
      settings.workerEndTime = moment.duration(this.defaultEndTime);
    }

    this.workerStartTime = moment().startOf('day').add(settings.workerStartTime).toDate();
    this.workerEndTime = moment().startOf('day').add(settings.workerEndTime).toDate();
    if (settings.firstNotificationBefore) {
      this.parseSenderValue(settings.firstNotificationBefore, 'firstNotificationPeriod', 'firstNotificationUnit');
    }

    if (settings.repeatNotificationEvery) {
      this.parseSenderValue(settings.repeatNotificationEvery, 'repeatNotificationPeriod', 'repeatNotificationUnit');
    } else {
      this.reminderSettingsForm.controls['enabledRepeatNotification'].setValue(false);
    }
  }

  saveChanges() {
    this.saveEmailSettings();
    this.router.navigate(['/management/communication/appointments/', { outlets: { 'action-panel': null } }]);
  }

  saveEmailSettings() {
    if (this.reminderSettingsForm.controls['template'].value)
      this.setting.createdFromEmailTemplateId = this.reminderSettingsForm.controls['template'].value.id;
    this.setting.senderName = this.reminderSettingsForm.controls['emailSenderName'].value;
    this.setting.senderEmail = this.reminderSettingsForm.controls['emailSenderEmail'].value;
    this.setting.emailSubjectLine = this.reminderSettingsForm.controls['emailSubjectLine'].value;
    this.setting.workerStartTime = this.stripTimeAsDuration(this.workerStartTime);
    this.setting.workerEndTime = this.stripTimeAsDuration(this.workerEndTime);
    this.setting.firstNotificationBefore = this.formatSenderValue('firstNotificationPeriod', 'firstNotificationUnit');
    if (this.reminderSettingsForm.controls['enabledRepeatNotification'].value) {
      this.setting.repeatNotificationEvery = this.formatSenderValue(
        'repeatNotificationPeriod',
        'repeatNotificationUnit'
      );
    }

    this.emailEditor.exportHtml((htmlObj: any) => {
      this.emailEditor.editor.saveDesign((definition) => {
        this.setting.emailHtml = htmlObj.html;
        this.setting.emailJson = JSON.stringify(definition);
        // Update virtual template in list
        if (this.emailTemplates.length >= 1 && this.emailTemplates[0].id == 0) {
          this.emailTemplates[0].name = this.setting.senderName + "'s Saved Template";
          this.emailTemplates[0].definition = this.setting.emailJson;
          this.emailTemplates[0].html = this.setting.emailHtml;
        } else {
          this.addVirtualTemplateToList(this.setting);
        }
        this.communicationsService.updateEmailNotificationsSettings(this.setting).subscribe(
          () => {
            this.communicationsService.refreshRequired = true;
            this.communicationsService.communicationsSettingsUpdated.next();
            this.showSuccessMessage = true;
            this.zone.run(() => (this.loading = false));
            this.reminderSettingsForm.markAsPristine();
            setTimeout(async () => {
              this.showSuccessMessage = false;
            }, 5000);
          },
          (error) => {
            this.loading = false;
            console.error(error);
          }
        );
      });
    });
  }

  addVirtualTemplateToList(settings: EmailCommunicationSetting) {
    this.emailTemplates.unshift({
      id: 0,
      emailTemplateType: 2,
      name: settings.senderName + "'s Saved Template",
      description: '',
      definition: settings.emailJson,
      html: settings.emailHtml,
      createdByUser: null,
      createdDate: new Date(),
      modifiedByUser: null,
      modifiedDate: new Date(),
    });
  }

  cancelUpdate() {
    this.communicationsService.refreshRequired = false;
    this.communicationsService.communicationsSettingsUpdated.next();
    this.router.navigate(['/management/communication/appointments/', { outlets: { 'action-panel-secondary': null } }]);
  }

  onStartTimeChange(date: Date) {
    this.minEndTime = moment(date).add(this.minimumDuration.toString(), 'minutes').toDate();
  }

  private parseSenderValue(value: string, periodCtrl: string, unitCtrl: string) {
    const split = value.split(' ');
    if (split.length === 1) {
      this.reminderSettingsForm.controls[periodCtrl].setValue(1);
      this.reminderSettingsForm.controls[unitCtrl].setValue(split[0] + 's');
    } else {
      this.reminderSettingsForm.controls[periodCtrl].setValue(split[0]);
      this.reminderSettingsForm.controls[unitCtrl].setValue(split[1]);
    }
  }

  private formatSenderValue(periodCtrl: string, unitCtrl: string): string {
    const value = this.reminderSettingsForm.controls[periodCtrl].value;
    const unit = this.reminderSettingsForm.controls[unitCtrl].value;
    if (value === 1) {
      return unit.substr(0, unit.length - 1);
    }
    return value + ' ' + unit;
  }

  private stripTimeAsDuration(dateTime: Date): moment.Duration {
    const startStr = dateTime.getHours() + ':' + dateTime.getMinutes() + ':00';
    return moment.duration(startStr);
  }

  onEditorLoad() {
    if (this.emailDesignDef) this.emailEditor.editor.loadDesign(this.emailDesignDef);
    if (this.iPad) this.emailEditor.editor.showPreview('Mobile');
  }

  revertToOriginalTemplate() {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '330px',
      data: {
        title: 'Revert to Original Template',
        content:
          'Are you sure you want to revert to the original email template definition? All your changes will be lost.',
        confirmButtonText: 'Yes',
        showCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'confirm') {
          this.loading = true;
          var selectedTemplateId = this.reminderSettingsForm.controls['template'].value.id;
          let selectedTemplate = this.emailTemplates.find((_) => _.id === selectedTemplateId);
          if (selectedTemplate) {
            this.loadEmailDef(selectedTemplate.definition);
          }
          this.loading = false;
        }
      });
  }

  sendTest() {
    const sendTestModal = this.modalService.open(SendTestEmailComponent, {
      centered: true,
      windowClass: 'send-test-modal',
    });
    sendTestModal.componentInstance.type = 'email';
    sendTestModal.componentInstance.quickView = true;
    sendTestModal.componentInstance.emailTemplateType = EmailTemplateType.AppointmentReminder;
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
